import React, { useState, useEffect, useRef, useCallback } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { motion, AnimatePresence } from "framer-motion";
import { Box, Heading, Text, Flex, CircularProgress, useInterval } from "@chakra-ui/react";

import TiliaAPIError from "api/TiliaAPIError";
import { usePollKYCStatus } from "api/kyc";
import EmptyState from "components/EmptyState";
import { IframeOrFullPageWrapper } from "components/Container";
import { useNavigatorOnline, useAnalytics } from "utils";
import { RETRY_RULE_CODE_MESSAGES } from "../utils";

import Success from "../Success";

const PROCESSING_TIMEOUT_DURATION = 60000;

/**
 * Component that handles the polling of KYC submission status. When the returned state of the KYC is "ACCEPT"
 * it returns the <Success /> component (see above). This component also handles the possibility of the user
 * being offline and notifying them.
 * @name PostSubmissionPolling
 * @param {object} props
 * @param {String} props.kycId
 * @param {Function} props.onComplete
 * @param {OnError} props.onError
 * @param {Function} [props.onRetryRequired]
 * @param {number} [props.processingTimeout = PROCESSING_TIMEOUT_DURATION] - only passing in as prop to allow for shortening in tests
 * @author magnus
 * @returns JSX.Element
 */
export default function PostSubmissionPolling({
  kycId,
  onComplete,
  onError,
  onRetryRequired,
  processingTimeout = PROCESSING_TIMEOUT_DURATION, // only passing in as prop to allow for shortening in tests
  loadingMessages,
}) {
  if (!kycId) {
    throw new Error("KYC ID missing while trying to poll for identity verification status.");
  }

  const { formatMessage } = useIntl();
  const { isOffline } = useNavigatorOnline();
  const [statusMessageInterval, setStatusMessageInterval] = useState(5000);
  const [messageIndex, setMessageIndex] = useState(0);
  const [applicationState, setApplicationState] = useState(null);
  const { data, error, isError } = usePollKYCStatus(kycId, !applicationState);
  const analytics = useAnalytics();
  const viewEventFired = useRef(false);

  const tokenInvalid = error instanceof TiliaAPIError && error.status === 401;

  const defaultLoadingMessages = [
    formatMessage({ defaultMessage: "Processing...", description: "Loading message" }),
    formatMessage({ defaultMessage: "Uploading info...", description: "Loading message" }),
    formatMessage({
      defaultMessage: "Dotting the i's and crossing the t's...",
      description:
        'American expression meaning "attention to detail". The expression is being used here as a loading message to indicate work is being done.',
    }),
    formatMessage({ defaultMessage: "Verifying information...", description: "Loading message" }),
    formatMessage({
      defaultMessage:
        "Hmm, this is taking awhile. We'll keep waiting for results, but you'll also receive an email once we verify your identity.",
      description: "Loading message",
    }),
  ];

  const defaultedLoadingMessages = loadingMessages || defaultLoadingMessages;
  const getRetryMessage = useCallback(() => {
    const retryMsg =
      RETRY_RULE_CODE_MESSAGES[data?.payload?.tilia_retry_rule_code] || "Uknown error";
    return retryMsg;
  }, [data]);

  useEffect(() => {
    // useEffect for tracking page view analytic
    if (!viewEventFired.current) {
      analytics.page("KYC", "KYC_PostSubmissionPolling_Viewed");
      viewEventFired.current = true;
    }
  }, [analytics, viewEventFired]);

  useEffect(() => {
    /**
     * This useEffect is to timeout if stuck
     * in PROCESSING state and fire an onError.
     * Since react-query's retryInterval will guard
     * against us getting updated 'data' values when
     * retry keeps getting 'PROCESSING', we can fire off
     * the setTimeout and expect it to not get reset
     * on polling so long as the value is the same.
     */
    const isProcessing = !isError && data?.payload?.state === "PROCESSING";
    let timeout = null;
    if (isProcessing) {
      timeout = setTimeout(() => {
        if (onError) {
          onError({ message: "KYC application stuck in PROCESSING state." });
        }
      }, processingTimeout);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [data, isError, onError, processingTimeout]);

  // Loop through our loading messages to display to the user
  useInterval(() => {
    if (messageIndex >= loadingMessages.length - 1) {
      setStatusMessageInterval(null);
    } else {
      setMessageIndex((currentIndex) => currentIndex + 1);
    }
  }, statusMessageInterval);

  // Wait for the KYC state to become something other than "PROCESSING"
  useEffect(() => {
    if (
      !isError &&
      typeof data?.payload?.state === "string" &&
      data.payload.state.toUpperCase() !== "PROCESSING"
    ) {
      const appState = data.payload.state.toUpperCase();
      const retryMsg = getRetryMessage();
      if (appState === "SYSTEM-CANCELLED" && onRetryRequired && retryMsg) {
        onRetryRequired(retryMsg);
      } else {
        setApplicationState(appState);
        // setApplicationState(data.payload.state.toUpperCase());
      }
    } else if (tokenInvalid) {
      // If the user waits for quite some time and their token becomes invalid,
      // we don't want to show them a generic error message but instead, show
      // them a success screen with "We'll be in touch" messaging. This also
      // stops the polling, since applicationState becomes truthy.
      setApplicationState("INVALID_TOKEN");
    }
  }, [data, isError, tokenInvalid, getRetryMessage, onRetryRequired]);

  // const handleCloseButton = () => {
  //   onComplete({
  //     state: FLOW_STATE.CANCEL,
  //     trigger: "close-button",
  //   });
  // };

  if (isOffline) {
    return (
      <EmptyState
        title={formatMessage({
          defaultMessage: "No connection!",
          description: "Error page title",
        })}
        description={formatMessage({
          defaultMessage:
            "You seem to be offline. We'll resume the process of checking your identity verification status when you're back online.",
          description: "Error message",
        })}
      />
    );
  }

  if (isError && !tokenInvalid) {
    return <EmptyState error={error} />;
  }

  if (applicationState === "SYSTEM-CANCELLED") {
    // this is handled above in the useEffect
    return <></>;
  }

  if (applicationState) {
    return <Success applicationState={applicationState} onComplete={onComplete} />;
  }

  return (
    <IframeOrFullPageWrapper>
      <Box px={4} pb={6}>
        <Heading mb={2}>
          <FormattedMessage defaultMessage="Verifying identity" description="Page header" />
        </Heading>
        <Text fontSize="sm" color="secondaryText">
          <FormattedMessage
            defaultMessage="This should be quick. We’ll also send you an email."
            description="User message"
          />
        </Text>
        <Flex mt={6} flexDirection="column" alignItems="center" justifyContent="center">
          <CircularProgress isIndeterminate color="integrator.500" />

          <AnimatePresence initial={false} exitBeforeEnter>
            <motion.div
              key={`loading-status-${messageIndex}`}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
            >
              <Text fontSize="sm" color="primaryText" mt={4}>
                {defaultedLoadingMessages[messageIndex] ||
                  formatMessage({
                    defaultMessage:
                      "Hmm, this is taking a while. Feel free to close this page and we'll send you an email with any updates.",
                    description: "Loading message",
                  })}
              </Text>
            </motion.div>
          </AnimatePresence>

          {/* Holding on to button in code as showing the button will become an option */}
          {/* <Button
            onClick={handleCloseButton}
            variant="outline"
            colorScheme="gray"
            mt={6}
            width="full"
          >
            Close
          </Button> */}
        </Flex>
      </Box>
    </IframeOrFullPageWrapper>
  );
}
